import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Download,
  Eye,
  FilePlus2,
  FileArchive,
  FileText,
  Filter,
  FolderPlus,
  Folders,
  ImageOff,
  ImagePlus,
  LayoutGrid,
  Link2,
  List,
  Loader2,
  MapPin,
  MoveRight,
  Pencil,
  Search,
  Tag,
  Sparkles,
  Play,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  AnaliseZip,
  apiService,
  ImportacaoZipResultado,
  OrigemDataLevantamento,
  RevisaoFotoStatus,
  UploadedFoto,
  UploadedMachineFolder,
} from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { VisualizadorVideoModal } from './VisualizadorVideoModal';
import { ConfirmarGeracaoIAModal } from '@/features/apreciacao-ia/ConfirmarGeracaoIAModal';
import { JobProgressModal } from '@/features/apreciacao-ia/JobProgressModal';
import type { StatusIaPorPasta } from '@/types/apreciacao-ia';
import { jobEstaAtivo } from '@/types/apreciacao-ia';
import { RevisaoLegadoModal } from './RevisaoLegadoModal';
import { resumoRevisao, temFotoPendente, vereditoDaPasta } from './legado-revisao';

/** Sentinela do grupo "sem área": só a chave do agrupamento, o rótulo é traduzido na hora de exibir. */
const SEM_AREA = '__sem_area__';

const STATUS_A_FAZER = 'a_fazer';
const STATUS_CANCELADO = 'cancelado';
const STATUS_NAO_NR12 = 'nao_e_nr12';
/** Status que tiram a pasta do escopo de apreciação (não contam nas métricas nem no filtro de pendentes). */
const STATUS_FORA_ESCOPO: string[] = [STATUS_CANCELADO, STATUS_NAO_NR12];

/**
 * Status manuais de uma pasta, com estilo do badge no cartão. `value` é o que
 * vai para a API; o rótulo mora no dicionário e é resolvido em `labelChave`.
 */
const STATUS_OPTIONS: Array<{ value: string; labelChave: string; badgeClass: string; dotClass: string }> = [
  {
    // Removido automaticamente quando uma apreciação é criada/vinculada à pasta.
    value: STATUS_A_FAZER,
    labelChave: 'imagens.acervo.status.aFazer',
    badgeClass: 'bg-sky-100 text-sky-700 border border-sky-300',
    dotClass: 'bg-sky-500',
  },
  {
    value: 'sem_foto_maquina',
    labelChave: 'imagens.acervo.status.semFotoMaquina',
    badgeClass: 'bg-rose-100 text-rose-700 border border-rose-300',
    dotClass: 'bg-rose-500',
  },
  {
    value: 'sem_foto_painel',
    labelChave: 'imagens.acervo.status.semFotoPainel',
    badgeClass: 'bg-orange-100 text-orange-700 border border-orange-300',
    dotClass: 'bg-orange-500',
  },
  {
    value: 'incompleto',
    labelChave: 'imagens.acervo.status.incompleto',
    badgeClass: 'bg-amber-100 text-amber-700 border border-amber-300',
    dotClass: 'bg-amber-500',
  },
  {
    value: STATUS_NAO_NR12,
    labelChave: 'imagens.acervo.status.naoNr12',
    badgeClass: 'bg-violet-100 text-violet-700 border border-violet-300',
    dotClass: 'bg-violet-500',
  },
  {
    value: STATUS_CANCELADO,
    labelChave: 'imagens.acervo.status.cancelado',
    badgeClass: 'bg-gray-200 text-gray-600 border border-gray-300',
    dotClass: 'bg-gray-500',
  },
];

const statusOption = (status?: string | null) => STATUS_OPTIONS.find((opt) => opt.value === status) || null;

function getApiBaseUrl() {
  const envUrl =
    import.meta.env.VITE_API_URL ??
    '/api';
  return envUrl.replace(/\/$/, '');
}

function toAbsoluteUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;

  const apiBase = getApiBaseUrl();
  const apiOrigin = apiBase.startsWith('http') ? apiBase.replace(/\/api$/, '') : '';

  if (url.startsWith('/api/')) {
    if (apiBase.startsWith('http')) return `${apiBase}${url.slice(4)}`;
    return url;
  }

  if (url.startsWith('/uploads/')) {
    if (apiOrigin) return `${apiOrigin}${url}`;
    return url;
  }

  return url;
}

function withPreviewParams(url: string, width: number, quality: number): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}w=${width}&q=${quality}`;
}

/**
 * Como cada origem da data de levantamento se apresenta no cartão. O rótulo
 * precisa deixar claro o que a data é de fato: uma data inferida do arquivo não
 * pode aparecer com a mesma cara de uma registrada em campo, senão vira dado de
 * relatório sem ninguém saber que é chute.
 */
const ORIGEM_LEVANTAMENTO: Record<
  OrigemDataLevantamento,
  { rotulo: string; classe: string; tituloChave: string }
> = {
  captura: {
    rotulo: '',
    classe: 'text-emerald-600 dark:text-emerald-400',
    tituloChave: 'imagens.acervo.levantamento.captura',
  },
  upload: {
    rotulo: '',
    classe: 'text-schmersal-gray-medium',
    tituloChave: 'imagens.acervo.levantamento.upload',
  },
  inferida: {
    rotulo: '~',
    classe: 'text-schmersal-gray-medium',
    tituloChave: 'imagens.acervo.levantamento.inferida',
  },
  importacao: {
    rotulo: '~',
    classe: 'text-amber-600 dark:text-amber-500',
    tituloChave: 'imagens.acervo.levantamento.importacao',
  },
};

/**
 * Data do levantamento em campo da pasta. Fica escondida quando não há data
 * nenhuma (pasta vazia) — melhor ausência do que um traço sem significado.
 */
function DataLevantamento({ folder }: { folder: UploadedMachineFolder }) {
  const { t, formatarData } = useI18n();
  if (!folder.dataLevantamento) return null;
  const data = new Date(folder.dataLevantamento);
  if (Number.isNaN(data.getTime())) return null;
  const formatada = formatarData(data, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const origem = ORIGEM_LEVANTAMENTO[folder.origemDataLevantamento ?? 'inferida'];

  return (
    <p
      className={`text-xs mt-0.5 flex items-center gap-1 ${origem.classe}`}
      title={t(origem.tituloChave)}
    >
      <CalendarDays className="h-3 w-3 shrink-0" />
      {/* O `~` é marca de data estimada, cola na data e não é texto traduzível. */}
      <span>{t('imagens.acervo.levantamento.rotulo', { data: `${origem.rotulo}${formatada}` })}</span>
    </p>
  );
}

/**
 * Renderiza um texto já traduzido pondo `destaque` em negrito no lugar do
 * marcador `{{destaque}}` — assim a frase inteira continua numa chave só e a
 * ordem das palavras pode mudar entre idiomas.
 */
function TextoComDestaque({ texto, destaque }: { texto: string; destaque: string }) {
  const partes = texto.split('{{destaque}}');
  if (partes.length === 1) return <>{texto}</>;
  return (
    <>
      {partes[0]}
      <strong>{destaque}</strong>
      {partes.slice(1).join('{{destaque}}')}
    </>
  );
}

/** Tamanho de arquivo: a unidade é sigla técnica, só o número muda de locale. */
function formatBytes(bytes: number, formatarNumero: (valor: number, opcoes?: Intl.NumberFormatOptions) => string) {
  const units = ['B', 'KB', 'MB', 'GB'];
  if (!bytes) return `${formatarNumero(0)} B`;
  const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const casas = idx === 0 ? 0 : 1;
  return `${formatarNumero(bytes / 1024 ** idx, {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })} ${units[idx]}`;
}

export function ImagensMaquinasPage() {
  const { projeto } = useProjeto();
  const { t, formatarNumero } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const [downloadingFolder, setDownloadingFolder] = useState<string | null>(null);
  const [uploadingFolder, setUploadingFolder] = useState<string | null>(null);
  /** Pasta de legado sendo promovida para as pastas atuais. */
  const [migrandoFolder, setMigrandoFolder] = useState<string | null>(null);
  /** Importação de ZIP em andamento (percentual < 100 = enviando). */
  const [importandoZip, setImportandoZip] = useState<{ nome: string; percentual: number } | null>(null);
  const [resultadoZip, setResultadoZip] = useState<ImportacaoZipResultado | null>(null);
  /** ZIP já enviado, aguardando o usuário conferir o mapeamento e confirmar. */
  const [analiseZip, setAnaliseZip] = useState<AnaliseZip | null>(null);
  const [confirmandoZip, setConfirmandoZip] = useState(false);
  const zipInputRef = useRef<HTMLInputElement>(null);
  const [deletingFolder, setDeletingFolder] = useState<string | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showPendentes, setShowPendentes] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [todasFolders, setTodasFolders] = useState<UploadedMachineFolder[]>([]);
  const [semProjetoFolders, setSemProjetoFolders] = useState<UploadedMachineFolder[]>([]);
  const [openedFolder, setOpenedFolder] = useState<UploadedMachineFolder | null>(null);
  const [openedPhoto, setOpenedPhoto] = useState<UploadedFoto | null>(null);
  /** Vídeo aberto no player — abre o modal de captura de quadro, não o de imagem. */
  const [openedVideo, setOpenedVideo] = useState<UploadedFoto | null>(null);
  const [viewMode, setViewMode] = useState<'area' | 'list'>('area');
  const [collapsedAreas, setCollapsedAreas] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<Array<{ id: string; nome: string; tipo?: string }>>([]);
  const [templatesMaquina, setTemplatesMaquina] = useState<
    Array<{
      id: string;
      nome: string;
      tipoMaquina?: string | null;
      descricao?: string | null;
      grupo?: { id: string; nome: string } | null;
      totalPartes?: number;
      totalRiscos?: number;
    }>
  >([]);
  const [apreciacaoModalFolder, setApreciacaoModalFolder] = useState<UploadedMachineFolder | null>(null);
  const [creatingApreciacao, setCreatingApreciacao] = useState(false);
  const [apreciacoesExistentes, setApreciacoesExistentes] = useState<
    Array<{ id: string; numero?: string; titulo?: string; nomeMaquina?: string; maquina?: { nome?: string; area?: string } }>
  >([]);
  const [loadingApreciacoes, setLoadingApreciacoes] = useState(false);
  const [apreciacaoSearch, setApreciacaoSearch] = useState('');
  const [editingAreaFolder, setEditingAreaFolder] = useState<string | null>(null);
  const [areaDraft, setAreaDraft] = useState('');
  const [savingArea, setSavingArea] = useState(false);
  const [statusMenuFolder, setStatusMenuFolder] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState<string | null>(null);
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─────────────── Acervo LEGADO ───────────────
  // Fotos antigas migradas para o sistema. Vivem numa aba separada e passam por
  // uma revisão foto a foto antes de virarem escopo de apreciação.
  const [aba, setAba] = useState<'atuais' | 'legado'>('atuais');
  /** Filtro da aba Legado: só máquinas com foto ainda sem veredito. */
  const [showNaoRevisadas, setShowNaoRevisadas] = useState(false);
  /** Pasta aberta no revisor (guardada por nome para acompanhar recarregamentos). */
  const [revisandoFolder, setRevisandoFolder] = useState<string | null>(null);
  /** Pasta que receberá o upload disparado pelo input escondido. */
  const [uploadTarget, setUploadTarget] = useState<UploadedMachineFolder | null>(null);

  const folders = useMemo(() => todasFolders.filter((f) => !f.legado), [todasFolders]);
  const legadoFolders = useMemo(() => todasFolders.filter((f) => f.legado), [todasFolders]);
  const emLegado = aba === 'legado';

  const loadFolders = async (): Promise<UploadedMachineFolder[]> => {
    if (!projeto?.id) return [];
    setLoading(true);
    setError('');
    try {
      const response = await apiService.listarFotosUploaded(projeto.id);
      if (!response.success || !response.data) {
        setError(response.message || t('imagens.acervo.erro.carregar'));
        setTodasFolders([]);
        setSemProjetoFolders([]);
        return [];
      }
      const loadedFolders = response.data.maquinas || [];
      setTodasFolders(loadedFolders);
      setSemProjetoFolders(response.data.legado || []);
      setOpenedFolder((prev) => {
        if (!prev) return null;
        return loadedFolders.find((item) => item.machineFolder === prev.machineFolder) || prev;
      });
      // Mantém o modal de apreciação aberto sincronizado com os dados frescos.
      setApreciacaoModalFolder((prev) =>
        prev ? loadedFolders.find((item) => item.machineFolder === prev.machineFolder) || null : null
      );
      // Pastas de legado não geram apreciação por IA: ficam fora do polling.
      void carregarStatusIA(loadedFolders.filter((item) => !item.legado));
      return loadedFolders;
    } finally {
      setLoading(false);
    }
  };

  /** Lista de apreciações vinculadas a uma pasta (compatível com o campo legado). */
  const folderApreciacaoIds = (folder: UploadedMachineFolder): string[] =>
    folder.apreciacaoIds && folder.apreciacaoIds.length > 0
      ? folder.apreciacaoIds
      : folder.apreciacaoId
      ? [folder.apreciacaoId]
      : [];

  // ─────────────── Criação assistida por IA ───────────────
  // O job roda no servidor; aqui só disparamos e acompanhamos.

  /** Situação do job de IA por pasta, para os selos da grade. */
  const [statusIA, setStatusIA] = useState<Record<string, StatusIaPorPasta>>({});
  const [pastaParaIA, setPastaParaIA] = useState<UploadedMachineFolder | null>(null);
  const [jobIAAtivo, setJobIAAtivo] = useState<{ jobId: string; machineName: string } | null>(null);

  const carregarStatusIA = async (lista: UploadedMachineFolder[]) => {
    if (!projeto?.id || lista.length === 0) return;
    try {
      const r = await apiService.statusIaPorPasta(
        projeto.id,
        lista.map((f) => f.machineFolder)
      );
      if (r.success && r.data) {
        setStatusIA(Object.fromEntries(r.data.itens.map((i) => [i.machineFolder, i])));
      }
    } catch (e) {
      // Falha aqui só some com os selos; não deve quebrar a página.
      console.error('Erro ao carregar status de IA:', e);
    }
  };

  const podeGerarComIA = (folder: UploadedMachineFolder): { ok: boolean; motivo?: string } => {
    if (folder.totalFotos === 0) return { ok: false, motivo: t('imagens.encaixe.semFotos') };
    if (STATUS_FORA_ESCOPO.includes(folder.status || '')) {
      return { ok: false, motivo: t('imagens.encaixe.foraEscopo') };
    }
    return { ok: true };
  };

  const abrirAssistenteIA = (folder: UploadedMachineFolder) => {
    const statusAtual = statusIA[folder.machineFolder];
    // Job em andamento: mostra o progresso em vez de abrir o pré-voo de novo.
    if (statusAtual?.jobId && jobEstaAtivo(statusAtual.status)) {
      setJobIAAtivo({ jobId: statusAtual.jobId, machineName: folder.machineName });
      return;
    }
    setPastaParaIA(folder);
  };

  const loadTemplates = async () => {
    if (!projeto?.id) return;
    try {
      const [respSimples, respMaquina] = await Promise.all([
        apiService.listarTemplates({ projetoId: projeto.id, ativo: true, limit: 100 }),
        apiService.listarTemplateMultiPartes({ projetoId: projeto.id, ativo: true, limit: 100 }),
      ]);
      if (respSimples.success && respSimples.data) {
        setTemplates(Array.isArray(respSimples.data) ? respSimples.data : []);
      }
      if (respMaquina.success && respMaquina.data) {
        setTemplatesMaquina(Array.isArray(respMaquina.data) ? respMaquina.data : []);
      }
    } catch (e) {
      console.error('Erro ao carregar templates:', e);
    }
  };

  useEffect(() => {
    loadFolders();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projeto?.id]);

  /** Pendente: sem apreciação, sem marcação "A fazer" e dentro do escopo (não cancelada/não NR12). */
  const isPendente = (folder: UploadedMachineFolder) =>
    !((folder.apreciacaoIds && folder.apreciacaoIds.length > 0) || folder.apreciacaoId) &&
    folder.status !== STATUS_A_FAZER &&
    !STATUS_FORA_ESCOPO.includes(folder.status || '');

  const pendentesCount = useMemo(() => folders.filter(isPendente).length, [folders]);

  const filteredFolders = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const base = showPendentes ? folders.filter(isPendente) : folders;
    if (!normalized) return base;
    return base.filter(
      (folder) =>
        folder.machineName.toLowerCase().includes(normalized) ||
        (folder.area || '').toLowerCase().includes(normalized) ||
        (() => {
          const opcao = statusOption(folder.status);
          return opcao ? t(opcao.labelChave).toLowerCase().includes(normalized) : false;
        })()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, query, showPendentes]);

  // Pastas canceladas/não NR12 ficam fora das métricas de apreciação (inclusive do "Sem apreciação").
  const apreciacaoStats = useMemo(() => {
    const canceladas = folders.filter((folder) => folder.status === STATUS_CANCELADO).length;
    const naoNr12 = folders.filter((folder) => folder.status === STATUS_NAO_NR12).length;
    const ativas = folders.filter((folder) => !STATUS_FORA_ESCOPO.includes(folder.status || ''));
    const total = ativas.length;
    const comApreciacao = ativas.filter(
      (folder) => (folder.apreciacaoIds && folder.apreciacaoIds.length > 0) || folder.apreciacaoId
    ).length;
    return {
      total,
      canceladas,
      naoNr12,
      comApreciacao,
      semApreciacao: total - comApreciacao,
      percentual: total > 0 ? Math.round((comApreciacao / total) * 100) : 0,
    };
  }, [folders]);

  const naoRevisadasCount = useMemo(() => legadoFolders.filter(temFotoPendente).length, [legadoFolders]);

  const filteredLegado = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const base = showNaoRevisadas ? legadoFolders.filter(temFotoPendente) : legadoFolders;
    if (!normalized) return base;
    return base.filter(
      (folder) =>
        folder.machineName.toLowerCase().includes(normalized) ||
        (folder.area || '').toLowerCase().includes(normalized) ||
        (() => {
          const veredito = vereditoDaPasta(folder);
          return t(veredito.labelChave, veredito.labelVars).toLowerCase().includes(normalized);
        })()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legadoFolders, query, showNaoRevisadas]);

  /** Consolidado da revisão do legado (fotos e máquinas). */
  const legadoStats = useMemo(() => {
    let aprovadas = 0;
    let reprovadas = 0;
    let pendentes = 0;
    let concluidas = 0;
    let precisaMaisImagens = 0;
    for (const folder of legadoFolders) {
      const resumo = resumoRevisao(folder.fotos);
      aprovadas += resumo.aprovadas;
      reprovadas += resumo.reprovadas;
      pendentes += resumo.pendentes;
      const veredito = vereditoDaPasta(folder);
      if (veredito.value === 'aprovada') concluidas += 1;
      if (veredito.value === 'precisa_mais_imagens') precisaMaisImagens += 1;
    }
    const total = legadoFolders.length;
    const revisadas = concluidas + precisaMaisImagens;
    return {
      total,
      aprovadas,
      reprovadas,
      pendentes,
      concluidas,
      precisaMaisImagens,
      percentual: total > 0 ? Math.round((revisadas / total) * 100) : 0,
    };
  }, [legadoFolders]);

  const groupedByArea = useMemo(() => {
    const groups = new Map<string, UploadedMachineFolder[]>();
    for (const folder of filteredFolders) {
      const area = folder.area?.trim() || SEM_AREA;
      const bucket = groups.get(area);
      if (bucket) bucket.push(folder);
      else groups.set(area, [folder]);
    }
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === SEM_AREA) return 1;
      if (b[0] === SEM_AREA) return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [filteredFolders]);

  const toggleArea = (area: string) => {
    setCollapsedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(area)) next.delete(area);
      else next.add(area);
      return next;
    });
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    if (!projeto?.id) {
      setError(t('imagens.acervo.erro.semProjeto'));
      return;
    }
    setSavingFolder(true);
    setError('');
    try {
      // Na aba Legado a pasta já nasce marcada como acervo migrado.
      const response = await apiService.criarPastaFotos(projeto.id, newFolderName.trim(), emLegado);
      if (!response.success) {
        setError(response.message || t('imagens.acervo.erro.criarPasta'));
        return;
      }
      setNewFolderName('');
      await loadFolders();
    } finally {
      setSavingFolder(false);
    }
  };

  const handleDownloadFolder = async (folder: UploadedMachineFolder) => {
    if (!projeto?.id) return;
    setDownloadingFolder(folder.machineFolder);
    try {
      const blob = await apiService.downloadZipPastaFotos(projeto.id, folder.machineFolder);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folder.machineFolder}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('imagens.acervo.erro.baixarZip'));
    } finally {
      setDownloadingFolder(null);
    }
  };

  /**
   * Importa um ZIP (uma pasta por máquina, fotos dentro) no acervo da aba
   * aberta. Envio de GB, então o botão vira barra de progresso.
   */
  const handleImportarZip = async (file: File) => {
    if (!projeto?.id) return;
    setError('');
    setResultadoZip(null);
    setAnaliseZip(null);
    try {
      // Pergunta antes de abrir o envio: o servidor recusa por disco (507) sem
      // chegar a ler o corpo, e aí a barra ficaria parada em 0% sem dizer nada.
      const espaco = await apiService.verificarEspacoImportacaoZip(file.size);
      if (espaco.success && espaco.data && !espaco.data.cabe) {
        setError(espaco.data.mensagem);
        return;
      }

      setImportandoZip({ nome: file.name, percentual: 0 });
      const response = await apiService.importarZipFotos(projeto.id, file, emLegado, (percentual) =>
        setImportandoZip({ nome: file.name, percentual })
      );
      if (!response.success || !response.data) {
        setError(response.message || t('imagens.upload.zip.erroEnvio'));
        return;
      }
      // Nada foi gravado ainda: o usuário confere o mapeamento e confirma.
      setAnaliseZip(response.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('imagens.upload.zip.erroEnvioGenerico'));
    } finally {
      setImportandoZip(null);
    }
  };

  const handleConfirmarZip = async () => {
    if (!analiseZip) return;
    setConfirmandoZip(true);
    setError('');
    try {
      const response = await apiService.confirmarImportacaoZip(analiseZip.token);
      if (!response.success || !response.data) {
        setError(response.message || t('imagens.upload.zip.erroImportar'));
        return;
      }
      setAnaliseZip(null);
      setResultadoZip(response.data);
      await loadFolders();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('imagens.upload.zip.erroImportarGenerico'));
    } finally {
      setConfirmandoZip(false);
    }
  };

  const handleCancelarZip = async () => {
    if (!analiseZip) return;
    const token = analiseZip.token;
    setAnaliseZip(null);
    await apiService.cancelarImportacaoZip(token).catch(() => undefined);
  };

  /**
   * Promove uma máquina do legado para as pastas atuais. Só as aprovadas vão
   * junto — o resto fica arquivado no legado, então a ação não destrói nada.
   */
  const handleMigrarLegado = async (folder: UploadedMachineFolder) => {
    if (!projeto?.id) return;
    const resumo = resumoRevisao(folder.fotos);
    const confirmed = window.confirm(
      t('imagens.revisaoLegado.confirmarMigracao', {
        nome: folder.machineName,
        aprovadas: resumo.aprovadas,
        reprovadas: resumo.reprovadas,
        pendentes: resumo.pendentes,
      })
    );
    if (!confirmed) return;

    setMigrandoFolder(folder.machineFolder);
    setError('');
    try {
      const response = await apiService.migrarPastaLegado(projeto.id, folder.machineFolder);
      if (!response.success) {
        setError(response.message || t('imagens.revisaoLegado.erroMigrar'));
        return;
      }
      setRevisandoFolder((prev) => (prev === folder.machineFolder ? null : prev));
      await loadFolders();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('imagens.revisaoLegado.erroMigrarGenerico'));
    } finally {
      setMigrandoFolder(null);
    }
  };

  const handleDeleteFolder = async (folder: UploadedMachineFolder) => {
    if (!projeto?.id) return;
    const confirmed = window.confirm(
      t('imagens.acervo.confirmar.apagarPasta', { nome: folder.machineName, fotos: folder.totalFotos })
    );
    if (!confirmed) return;

    setDeletingFolder(folder.machineFolder);
    setError('');
    try {
      const response = await apiService.excluirPastaFotos(projeto.id, folder.machineFolder);
      if (!response.success) {
        setError(response.message || t('imagens.acervo.erro.apagarPasta'));
        return;
      }
      setOpenedFolder((prev) => (prev?.machineFolder === folder.machineFolder ? null : prev));
      await loadFolders();
    } finally {
      setDeletingFolder(null);
    }
  };

  const handleDeletePhoto = async (folder: UploadedMachineFolder, foto: UploadedFoto) => {
    if (!projeto?.id) return;
    const confirmed = window.confirm(t('imagens.acervo.confirmar.apagarFoto', { nome: foto.nomeArquivo }));
    if (!confirmed) return;

    setDeletingPhoto(foto.nomeArquivo);
    setError('');
    try {
      const response = await apiService.excluirFotoDaPasta(projeto.id, folder.machineFolder, foto.nomeArquivo);
      if (!response.success) {
        setError(response.message || t('imagens.acervo.erro.apagarFoto'));
        return;
      }
      setOpenedPhoto((prev) => (prev?.nomeArquivo === foto.nomeArquivo ? null : prev));
      if (folder.fotos.length <= 1) {
        setOpenedFolder(null);
      }
      await loadFolders();
    } finally {
      setDeletingPhoto(null);
    }
  };

  /** `abrirGaleria`: fluxo antigo mostra a pasta enquanto o envio acontece. */
  const handleSelectFiles = (folder: UploadedMachineFolder, abrirGaleria = true) => {
    if (abrirGaleria) setOpenedFolder(folder);
    setUploadTarget(folder);
    fileInputRef.current?.click();
  };

  const handleUploadFiles = async (files: FileList | null) => {
    const alvo = uploadTarget || openedFolder;
    if (!files || !alvo || !projeto?.id) return;
    const validFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
    if (validFiles.length === 0) {
      setError(t('imagens.upload.fotos.erroSelecao'));
      return;
    }

    setUploadingFolder(alvo.machineFolder);
    setError('');
    try {
      const response = await apiService.uploadFotosParaPasta(projeto.id, alvo.machineFolder, validFiles);
      if (!response.success) {
        setError(response.message || t('imagens.upload.fotos.erroEnvio'));
        return;
      }
      await loadFolders();
    } finally {
      setUploadingFolder(null);
      setUploadTarget(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /**
   * Grava o veredito de uma foto do legado. A lista é atualizada em memória
   * (e não com um reload inteiro) para não perder a posição da revisão.
   */
  const revisarFoto = async (
    folder: UploadedMachineFolder,
    fileName: string,
    status: RevisaoFotoStatus | null,
    motivo?: string
  ): Promise<boolean> => {
    if (!projeto?.id) return false;
    setError('');
    const response = await apiService.revisarFotoDaPasta(projeto.id, folder.machineFolder, fileName, status, motivo);
    if (!response.success) {
      setError(response.message || t('imagens.revisaoLegado.erroRevisar'));
      return false;
    }
    const revisao = response.data?.revisao ?? null;
    setTodasFolders((prev) =>
      prev.map((item) =>
        item.machineFolder !== folder.machineFolder
          ? item
          : {
              ...item,
              fotos: item.fotos.map((foto) =>
                foto.nomeArquivo === fileName ? { ...foto, revisao: revisao ?? undefined } : foto
              ),
            }
      )
    );
    return true;
  };

  const loadApreciacoesExistentes = async () => {
    if (!projeto?.id) return;
    setLoadingApreciacoes(true);
    try {
      const response = await apiService.listarApreciacoes({ projetoId: projeto.id, limit: 500 });
      if (response.success && Array.isArray(response.data)) {
        setApreciacoesExistentes(response.data);
      }
    } catch (e) {
      console.error('Erro ao carregar apreciações:', e);
    } finally {
      setLoadingApreciacoes(false);
    }
  };

  const openApreciacaoModal = (folder: UploadedMachineFolder) => {
    setApreciacaoModalFolder(folder);
    setApreciacaoSearch('');
    if (apreciacoesExistentes.length === 0) loadApreciacoesExistentes();
  };

  // Clique no selo: 1 vínculo abre o editor; 0 ou vários abrem o modal.
  const handleApreciacaoClick = (folder: UploadedMachineFolder) => {
    const ids = folderApreciacaoIds(folder);
    if (ids.length === 1) {
      navigate(`/apreciacoes/${ids[0]}/editar`);
    } else {
      openApreciacaoModal(folder);
    }
  };

  // Adiciona/remove um vínculo mantendo o modal aberto (multi-seleção).
  const toggleVinculoApreciacao = async (
    folder: UploadedMachineFolder,
    apreciacaoId: string,
    jaVinculada: boolean
  ) => {
    if (!projeto?.id) return;
    setCreatingApreciacao(true);
    setError('');
    try {
      // Envia a lista efetiva completa: persiste vínculos auto-resolvidos e
      // permite remover qualquer um sem reintrodução pela auto-resolução.
      const atuais = folderApreciacaoIds(folder);
      const novaLista = jaVinculada
        ? atuais.filter((id) => id !== apreciacaoId)
        : [...atuais, apreciacaoId];
      const response = await apiService.definirApreciacoesPasta(projeto.id, folder.machineFolder, novaLista);
      if (!response.success) {
        setError(response.message || t('imagens.acervo.apreciacaoModal.erroVinculo'));
        return;
      }
      await loadFolders();
    } finally {
      setCreatingApreciacao(false);
    }
  };

  const criarApreciacaoDaPasta = async (folder: UploadedMachineFolder, templateId?: string) => {
    if (!projeto?.id) return;
    setCreatingApreciacao(true);
    setError('');
    try {
      const response = await apiService.criarApreciacaoParaPasta(projeto.id, folder.machineFolder, templateId);
      if (response.success && response.data?.apreciacaoId) {
        navigate(`/apreciacoes/${response.data.apreciacaoId}/editar`);
      } else {
        setError(response.message || t('imagens.acervo.apreciacaoModal.erroCriar'));
        setApreciacaoModalFolder(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('imagens.acervo.apreciacaoModal.erroCriarGenerico'));
      setApreciacaoModalFolder(null);
    } finally {
      setCreatingApreciacao(false);
    }
  };

  // Modelos de máquina (templates multiparte): cria a apreciação em branco e aplica o modelo.
  const criarApreciacaoComModeloMaquina = async (folder: UploadedMachineFolder, templateMaquinaId: string) => {
    if (!projeto?.id) return;
    setCreatingApreciacao(true);
    setError('');
    try {
      const response = await apiService.criarApreciacaoParaPasta(projeto.id, folder.machineFolder);
      if (!response.success || !response.data?.apreciacaoId) {
        setError(response.message || t('imagens.acervo.apreciacaoModal.erroCriar'));
        setApreciacaoModalFolder(null);
        return;
      }
      const apreciacaoId = response.data.apreciacaoId;
      const aplicar = await apiService.aplicarTemplateMultiParte(templateMaquinaId, apreciacaoId);
      if (!aplicar.success) {
        // A apreciação já existe; segue para o editor avisando que o modelo não foi aplicado.
        setError(aplicar.message || t('imagens.acervo.apreciacaoModal.erroModelo'));
      }
      navigate(`/apreciacoes/${apreciacaoId}/editar`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('imagens.acervo.apreciacaoModal.erroCriarGenerico'));
      setApreciacaoModalFolder(null);
    } finally {
      setCreatingApreciacao(false);
    }
  };

  const startEditArea = (folder: UploadedMachineFolder) => {
    setEditingAreaFolder(folder.machineFolder);
    setAreaDraft(folder.area || '');
  };

  const cancelEditArea = () => {
    setEditingAreaFolder(null);
    setAreaDraft('');
  };

  const saveArea = async (folder: UploadedMachineFolder) => {
    if (!projeto?.id) return;
    setSavingArea(true);
    setError('');
    try {
      const response = await apiService.atualizarAreaPasta(projeto.id, folder.machineFolder, areaDraft.trim());
      if (!response.success) {
        setError(response.message || t('imagens.acervo.erro.salvarArea'));
        return;
      }
      setEditingAreaFolder(null);
      setAreaDraft('');
      await loadFolders();
    } finally {
      setSavingArea(false);
    }
  };

  const saveStatus = async (folder: UploadedMachineFolder, status: string | null) => {
    if (!projeto?.id) return;
    setStatusMenuFolder(null);
    setSavingStatus(folder.machineFolder);
    setError('');
    try {
      const response = await apiService.atualizarStatusPasta(projeto.id, folder.machineFolder, status);
      if (!response.success) {
        setError(response.message || t('imagens.acervo.erro.salvarStatus'));
        return;
      }
      await loadFolders();
    } finally {
      setSavingStatus(null);
    }
  };

  const startRename = (folder: UploadedMachineFolder) => {
    setRenamingFolder(folder.machineFolder);
    setRenameDraft(folder.machineName);
  };

  const cancelRename = () => {
    setRenamingFolder(null);
    setRenameDraft('');
  };

  const saveRename = async (folder: UploadedMachineFolder) => {
    if (!projeto?.id) return;
    const novoNome = renameDraft.trim();
    if (!novoNome || novoNome === folder.machineName) {
      cancelRename();
      return;
    }
    setSavingRename(true);
    setError('');
    try {
      const response = await apiService.renomearPastaFotos(projeto.id, folder.machineFolder, novoNome);
      if (!response.success) {
        setError(response.message || t('imagens.acervo.erro.renomearPasta'));
        return;
      }
      cancelRename();
      // O identificador da pasta muda; fecha modais que apontavam para o antigo.
      setOpenedFolder((prev) => (prev?.machineFolder === folder.machineFolder ? null : prev));
      setApreciacaoModalFolder((prev) => (prev?.machineFolder === folder.machineFolder ? null : prev));
      await loadFolders();
    } finally {
      setSavingRename(false);
    }
  };

  const knownAreas = useMemo(
    () =>
      Array.from(new Set(todasFolders.map((f) => f.area).filter((a): a is string => !!a && !!a.trim()))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [todasFolders]
  );

  const renderFolderCard = (folder: UploadedMachineFolder) => {
    const apreciacaoIds = folderApreciacaoIds(folder);
    const temApreciacao = apreciacaoIds.length > 0;
    const multiplasApreciacoes = apreciacaoIds.length > 1;
    const statusJobIA = statusIA[folder.machineFolder];
    const gerandoIA = jobEstaAtivo(statusJobIA?.status);
    const isEditingArea = editingAreaFolder === folder.machineFolder;
    const isRenaming = renamingFolder === folder.machineFolder;
    const status = statusOption(folder.status);
    const statusMenuOpen = statusMenuFolder === folder.machineFolder;
    return (
      <div
        key={folder.machineFolder}
        className="relative bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light overflow-hidden"
      >
        {/* Indicador de apreciação (azul = tem, cinza = não tem) */}
        <button
          type="button"
          onClick={() => (gerandoIA ? abrirAssistenteIA(folder) : handleApreciacaoClick(folder))}
          className={`absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm transition-colors ${
            gerandoIA
              ? 'bg-purple-600 text-white animate-pulse'
              : temApreciacao
              ? 'bg-schmersal-blue text-white hover:bg-schmersal-blue-dark'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-schmersal-gray-dark dark:text-schmersal-gray-light'
          }`}
          title={
            gerandoIA
              ? statusJobIA?.rotulo || t('imagens.encaixe.emAndamento')
              : temApreciacao
              ? multiplasApreciacoes
                ? t('imagens.acervo.card.gerenciarApreciacoes')
                : t('imagens.acervo.card.editarApreciacao')
              : t('imagens.acervo.card.criarApreciacao')
          }
        >
          {gerandoIA ? (
            <Sparkles className="h-3.5 w-3.5" />
          ) : temApreciacao ? (
            <ClipboardCheck className="h-3.5 w-3.5" />
          ) : (
            <ClipboardList className="h-3.5 w-3.5" />
          )}
          {gerandoIA
            ? t('imagens.encaixe.gerando', { percentual: statusJobIA?.percentual ?? 0 })
            : temApreciacao
            ? multiplasApreciacoes
              ? t('imagens.acervo.card.variasApreciacoes', { quantidade: apreciacaoIds.length })
              : t('imagens.acervo.card.apreciacao')
            : t('imagens.acervo.card.semApreciacao')}
        </button>

        {/* Status manual da pasta (badge + menu) */}
        <div className="absolute top-2 right-2 z-10">
          <button
            type="button"
            onClick={() => setStatusMenuFolder(statusMenuOpen ? null : folder.machineFolder)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm transition-colors ${
              status
                ? status.badgeClass
                : 'bg-white/85 text-gray-500 border border-gray-300 hover:bg-white dark:bg-schmersal-gray-dark/85 dark:text-schmersal-gray-light'
            }`}
            title={
              status
                ? t('imagens.acervo.status.alterar', { status: t(status.labelChave) })
                : t('imagens.acervo.status.definir')
            }
          >
            {savingStatus === folder.machineFolder ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Tag className="h-3.5 w-3.5" />
            )}
            {status ? t(status.labelChave) : t('comum.rotulo.status')}
          </button>
          {statusMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setStatusMenuFolder(null)} />
              <div className="absolute right-0 mt-1 z-20 w-52 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker shadow-lg overflow-hidden">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => saveStatus(folder, opt.value)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-schmersal-gray-light/50 dark:hover:bg-schmersal-gray-dark/50 ${
                      folder.status === opt.value ? 'font-semibold' : ''
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full shrink-0 ${opt.dotClass}`} />
                    <span className="flex-1 text-schmersal-gray-dark dark:text-white">{t(opt.labelChave)}</span>
                    {folder.status === opt.value && <Check className="h-4 w-4 text-schmersal-blue" />}
                  </button>
                ))}
                {folder.status && (
                  <button
                    type="button"
                    onClick={() => saveStatus(folder, null)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <X className="h-4 w-4" />
                    {t('imagens.acervo.status.remover')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <button type="button" className="w-full text-left" onClick={() => setOpenedFolder(folder)}>
          {folder.fotos.length === 0 ? (
            <div className="h-40 bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex flex-col items-center justify-center gap-1.5 text-schmersal-gray-medium">
              <ImageOff className="h-8 w-8 opacity-50" />
              <span className="text-xs">{t('imagens.acervo.card.semFotos')}</span>
            </div>
          ) : (
            <div className="h-40 bg-schmersal-gray-light dark:bg-schmersal-gray-dark overflow-hidden grid grid-cols-2 gap-0.5">
              {folder.fotos.slice(0, 4).map((foto) => (
                <img
                  key={foto.nomeArquivo}
                  src={withPreviewParams(toAbsoluteUrl(foto.previewUrl), 300, 50)}
                  alt={foto.nomeArquivo}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ))}
            </div>
          )}
        </button>

        <div className="p-3">
          {isRenaming ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={renameDraft}
                autoFocus
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRename(folder);
                  if (e.key === 'Escape') cancelRename();
                }}
                placeholder={t('imagens.acervo.card.renomearPlaceholder')}
                className="flex-1 min-w-0 px-2 py-1 text-sm font-semibold border rounded bg-white dark:bg-schmersal-gray-darkest"
              />
              <button
                type="button"
                onClick={() => saveRename(folder)}
                disabled={savingRename}
                className="p-1 rounded bg-schmersal-blue text-white disabled:opacity-50"
                title={t('imagens.acervo.card.salvarNome')}
              >
                {savingRename ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={cancelRename}
                className="p-1 rounded border text-schmersal-gray-medium"
                title={t('comum.acao.cancelar')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <p className="flex-1 min-w-0 font-semibold text-schmersal-gray-dark dark:text-white truncate">
                {folder.machineName}
              </p>
              <button
                type="button"
                onClick={() => startRename(folder)}
                className="p-1 rounded text-schmersal-gray-medium/60 hover:text-schmersal-blue hover:bg-schmersal-blue/10 shrink-0"
                title={t('imagens.acervo.card.renomear')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <p className="text-xs text-schmersal-gray-medium mt-1">
            {t('imagens.acervo.card.fotos', { quantidade: folder.totalFotos - (folder.totalVideos ?? 0) })}
            {folder.totalVideos ? t('imagens.acervo.card.videosSufixo', { quantidade: folder.totalVideos }) : ''}
          </p>
          <DataLevantamento folder={folder} />

          {/* Área (editável) */}
          {isEditingArea ? (
            <div className="mt-2 flex items-center gap-1">
              <input
                type="text"
                list="areas-conhecidas"
                value={areaDraft}
                autoFocus
                onChange={(e) => setAreaDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveArea(folder);
                  if (e.key === 'Escape') cancelEditArea();
                }}
                placeholder={t('imagens.acervo.card.areaPlaceholder')}
                className="flex-1 min-w-0 px-2 py-1 text-xs border rounded bg-white dark:bg-schmersal-gray-darkest"
              />
              <button
                type="button"
                onClick={() => saveArea(folder)}
                disabled={savingArea}
                className="p-1 rounded bg-schmersal-blue text-white disabled:opacity-50"
                title={t('imagens.acervo.card.salvarArea')}
              >
                {savingArea ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={cancelEditArea}
                className="p-1 rounded border text-schmersal-gray-medium"
                title={t('comum.acao.cancelar')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => startEditArea(folder)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-schmersal-gray-medium hover:text-schmersal-blue group"
              title={t('imagens.acervo.card.definirArea')}
            >
              <MapPin className="h-3.5 w-3.5" />
              <span className={folder.area ? '' : 'italic'}>{folder.area || t('imagens.acervo.card.definirArea')}</span>
              <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100" />
            </button>
          )}

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => handleSelectFiles(folder)}
              className="inline-flex items-center px-3 py-2 rounded border text-sm"
              title={t('imagens.acervo.card.adicionarFotos')}
            >
              {uploadingFolder === folder.machineFolder ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={() => handleDownloadFolder(folder)}
              disabled={folder.totalFotos === 0 || downloadingFolder === folder.machineFolder}
              className="inline-flex items-center px-3 py-2 rounded bg-schmersal-blue text-white text-sm disabled:opacity-50"
              title={
                folder.totalFotos === 0
                  ? t('imagens.acervo.card.pastaSemFotos')
                  : t('imagens.acervo.card.baixarZip')
              }
            >
              {downloadingFolder === folder.machineFolder ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </button>
            {(() => {
              // Roxo é a cor já convencionada para IA no projeto.
              const permissao = podeGerarComIA(folder);
              const statusJob = statusIA[folder.machineFolder];
              const gerando = jobEstaAtivo(statusJob?.status);
              return (
                <button
                  type="button"
                  onClick={() => abrirAssistenteIA(folder)}
                  disabled={!permissao.ok && !gerando}
                  className={`inline-flex items-center px-3 py-2 rounded text-sm text-white disabled:opacity-50 ${
                    gerando ? 'bg-purple-500 animate-pulse' : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                  title={
                    gerando
                      ? t('imagens.encaixe.gerando', { percentual: statusJob?.percentual ?? 0 })
                      : permissao.ok
                      ? temApreciacao
                        ? t('imagens.encaixe.gerarNova')
                        : t('imagens.encaixe.criar')
                      : permissao.motivo
                  }
                >
                  {gerando ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </button>
              );
            })()}
            <button
              type="button"
              onClick={() => openApreciacaoModal(folder)}
              className={`inline-flex items-center px-3 py-2 rounded border text-sm ${
                temApreciacao ? 'border-schmersal-blue text-schmersal-blue' : ''
              }`}
              title={
                temApreciacao
                  ? t('imagens.acervo.card.alterarApreciacao')
                  : t('imagens.acervo.card.vincularApreciacao')
              }
            >
              <Link2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleDeleteFolder(folder)}
              disabled={deletingFolder === folder.machineFolder}
              className="inline-flex items-center px-3 py-2 rounded border border-red-300 text-red-600 hover:bg-red-50 text-sm ml-auto disabled:opacity-50"
              title={t('imagens.acervo.card.apagarPasta')}
            >
              {deletingFolder === folder.machineFolder ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Cartão do acervo legado: o foco é a revisão das fotos migradas, então no
   * lugar dos selos de apreciação/IA aparece o veredito derivado dos vereditos
   * das fotos e o atalho para entrar na máquina e revisar.
   */
  const renderLegadoCard = (folder: UploadedMachineFolder) => {
    const resumo = resumoRevisao(folder.fotos);
    const veredito = vereditoDaPasta(folder);
    const isEditingArea = editingAreaFolder === folder.machineFolder;
    const isRenaming = renamingFolder === folder.machineFolder;
    return (
      <div
        key={folder.machineFolder}
        className="relative bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark overflow-hidden"
      >
        <span
          className={`absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm ${veredito.badgeClass}`}
          title={t(veredito.detalheChave, veredito.detalheVars)}
        >
          <span className={`h-2 w-2 rounded-full ${veredito.dotClass}`} />
          {t(veredito.labelChave, veredito.labelVars)}
        </span>
        <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-schmersal-gray-dark/85 text-white text-[11px] font-semibold">
          <Archive className="h-3 w-3" />
          {t('imagens.revisaoLegado.card.selo')}
        </span>

        <button
          type="button"
          className="w-full text-left"
          onClick={() => setRevisandoFolder(folder.machineFolder)}
          title={t('imagens.revisaoLegado.card.abrir')}
        >
          {folder.fotos.length === 0 ? (
            <div className="h-40 bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex flex-col items-center justify-center gap-1.5 text-schmersal-gray-medium">
              <ImageOff className="h-8 w-8 opacity-50" />
              <span className="text-xs">{t('imagens.revisaoLegado.card.semFotos')}</span>
            </div>
          ) : (
            <div className="h-40 bg-schmersal-gray-light dark:bg-schmersal-gray-dark overflow-hidden grid grid-cols-2 gap-0.5">
              {folder.fotos.slice(0, 4).map((foto) => (
                <div key={foto.nomeArquivo} className="relative">
                  <img
                    src={withPreviewParams(toAbsoluteUrl(foto.previewUrl), 300, 50)}
                    alt={foto.nomeArquivo}
                    loading="lazy"
                    className={`w-full h-full object-cover ${foto.revisao?.status === 'reprovada' ? 'opacity-40' : ''}`}
                  />
                  {foto.revisao && (
                    <span
                      className={`absolute top-1 right-1 h-4 w-4 rounded-full flex items-center justify-center text-white ${
                        foto.revisao.status === 'aprovada' ? 'bg-emerald-500' : 'bg-rose-500'
                      }`}
                    >
                      {foto.revisao.status === 'aprovada' ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </button>

        <div className="p-3">
          {isRenaming ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={renameDraft}
                autoFocus
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRename(folder);
                  if (e.key === 'Escape') cancelRename();
                }}
                placeholder={t('imagens.acervo.card.renomearPlaceholder')}
                className="flex-1 min-w-0 px-2 py-1 text-sm font-semibold border rounded bg-white dark:bg-schmersal-gray-darkest"
              />
              <button
                type="button"
                onClick={() => saveRename(folder)}
                disabled={savingRename}
                className="p-1 rounded bg-schmersal-blue text-white disabled:opacity-50"
                title={t('imagens.acervo.card.salvarNome')}
              >
                {savingRename ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={cancelRename}
                className="p-1 rounded border text-schmersal-gray-medium"
                title={t('comum.acao.cancelar')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <p className="flex-1 min-w-0 font-semibold text-schmersal-gray-dark dark:text-white truncate">
                {folder.machineName}
              </p>
              <button
                type="button"
                onClick={() => startRename(folder)}
                className="p-1 rounded text-schmersal-gray-medium/60 hover:text-schmersal-blue hover:bg-schmersal-blue/10 shrink-0"
                title={t('imagens.acervo.card.renomear')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <p className="text-xs text-schmersal-gray-medium mt-1">
            {t('imagens.revisaoLegado.card.resumo', {
              total: resumo.total,
              aprovadas: resumo.aprovadas,
              reprovadas: resumo.reprovadas,
            })}
            {resumo.pendentes > 0 &&
              t('imagens.revisaoLegado.card.resumoPendentes', { pendentes: resumo.pendentes })}
          </p>

          {/* Progresso da revisão (aprovadas em verde, reprovadas em vermelho) */}
          {resumo.total > 0 && (
            <div className="mt-2 h-1.5 rounded-full bg-schmersal-gray-light dark:bg-schmersal-gray-dark overflow-hidden flex">
              <div
                className="h-full bg-emerald-500"
                style={{ width: `${(resumo.aprovadas / resumo.total) * 100}%` }}
              />
              <div
                className="h-full bg-rose-500"
                style={{ width: `${(resumo.reprovadas / resumo.total) * 100}%` }}
              />
            </div>
          )}

          {isEditingArea ? (
            <div className="mt-2 flex items-center gap-1">
              <input
                type="text"
                list="areas-conhecidas"
                value={areaDraft}
                autoFocus
                onChange={(e) => setAreaDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveArea(folder);
                  if (e.key === 'Escape') cancelEditArea();
                }}
                placeholder={t('imagens.acervo.card.areaPlaceholder')}
                className="flex-1 min-w-0 px-2 py-1 text-xs border rounded bg-white dark:bg-schmersal-gray-darkest"
              />
              <button
                type="button"
                onClick={() => saveArea(folder)}
                disabled={savingArea}
                className="p-1 rounded bg-schmersal-blue text-white disabled:opacity-50"
                title={t('imagens.acervo.card.salvarArea')}
              >
                {savingArea ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={cancelEditArea}
                className="p-1 rounded border text-schmersal-gray-medium"
                title={t('comum.acao.cancelar')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => startEditArea(folder)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-schmersal-gray-medium hover:text-schmersal-blue group"
              title={t('imagens.acervo.card.definirArea')}
            >
              <MapPin className="h-3.5 w-3.5" />
              <span className={folder.area ? '' : 'italic'}>{folder.area || t('imagens.acervo.card.definirArea')}</span>
              <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100" />
            </button>
          )}

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setRevisandoFolder(folder.machineFolder)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded bg-schmersal-blue text-white text-sm font-medium"
              title={t('imagens.revisaoLegado.card.revisarTitulo')}
            >
              <Eye className="h-4 w-4" />
              {resumo.pendentes > 0
                ? t('imagens.revisaoLegado.card.revisarPendentes', { pendentes: resumo.pendentes })
                : t('imagens.revisaoLegado.card.revisar')}
            </button>
            <button
              type="button"
              onClick={() => handleSelectFiles(folder, false)}
              className="inline-flex items-center px-3 py-2 rounded border text-sm"
              title={t('imagens.revisaoLegado.enviarFotosMigradas')}
            >
              {uploadingFolder === folder.machineFolder ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="h-4 w-4" />
              )}
            </button>
            {/* Promover para as pastas atuais: só faz sentido depois que a
                revisão aprovou pelo menos uma foto. */}
            <button
              type="button"
              onClick={() => handleMigrarLegado(folder)}
              disabled={resumo.aprovadas === 0 || migrandoFolder === folder.machineFolder}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm disabled:opacity-50"
              title={
                resumo.aprovadas === 0
                  ? t('imagens.revisaoLegado.card.migrarBloqueado')
                  : t('imagens.revisaoLegado.card.migrarTitulo', { aprovadas: resumo.aprovadas })
              }
            >
              {migrandoFolder === folder.machineFolder ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MoveRight className="h-4 w-4" />
              )}
              {t('imagens.revisaoLegado.card.migrar')}
            </button>
            <button
              type="button"
              onClick={() => handleDownloadFolder(folder)}
              disabled={folder.totalFotos === 0 || downloadingFolder === folder.machineFolder}
              className="inline-flex items-center px-3 py-2 rounded border text-sm disabled:opacity-50"
              title={
                folder.totalFotos === 0
                  ? t('imagens.acervo.card.pastaSemFotos')
                  : t('imagens.acervo.card.baixarZip')
              }
            >
              {downloadingFolder === folder.machineFolder ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={() => handleDeleteFolder(folder)}
              disabled={deletingFolder === folder.machineFolder}
              className="inline-flex items-center px-3 py-2 rounded border border-red-300 text-red-600 hover:bg-red-50 text-sm disabled:opacity-50"
              title={t('imagens.acervo.card.apagarPasta')}
            >
              {deletingFolder === folder.machineFolder ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!projeto?.id) {
    return (
      <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
        {t('imagens.acervo.semProjeto')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        className="hidden"
        onChange={(e) => handleUploadFiles(e.target.files)}
      />

      <input
        ref={zipInputRef}
        type="file"
        accept=".zip,application/zip"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleImportarZip(file);
        }}
      />

      <datalist id="areas-conhecidas">
        {knownAreas.map((area) => (
          <option key={area} value={area} />
        ))}
      </datalist>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white">
            {t('imagens.acervo.titulo')}
          </h1>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            {emLegado ? t('imagens.acervo.subtituloLegado') : t('imagens.acervo.subtitulo')}
          </p>
        </div>

        {/* Alternância de visualização (só faz sentido na grade das pastas atuais) */}
        <div
          className={`inline-flex rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark overflow-hidden self-start ${
            emLegado ? 'hidden' : ''
          }`}
        >
          <button
            type="button"
            onClick={() => setViewMode('area')}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium ${
              viewMode === 'area'
                ? 'bg-schmersal-blue text-white'
                : 'bg-white dark:bg-schmersal-gray-darker text-schmersal-gray-dark dark:text-schmersal-gray-light'
            }`}
            title={t('imagens.acervo.visao.porAreaTitulo')}
          >
            <LayoutGrid className="h-4 w-4" />
            {t('imagens.acervo.visao.porArea')}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium ${
              viewMode === 'list'
                ? 'bg-schmersal-blue text-white'
                : 'bg-white dark:bg-schmersal-gray-darker text-schmersal-gray-dark dark:text-schmersal-gray-light'
            }`}
            title={t('imagens.acervo.visao.listaTitulo')}
          >
            <List className="h-4 w-4" />
            {t('imagens.acervo.visao.lista')}
          </button>
        </div>
      </div>

      {/* Atuais x Legado: acervos com regras diferentes, então não se misturam
          na mesma grade nem nas mesmas métricas. */}
      <div className="inline-flex rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark overflow-hidden">
        <button
          type="button"
          onClick={() => setAba('atuais')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium ${
            !emLegado
              ? 'bg-schmersal-blue text-white'
              : 'bg-white dark:bg-schmersal-gray-darker text-schmersal-gray-dark dark:text-schmersal-gray-light'
          }`}
        >
          <Folders className="h-4 w-4" />
          {t('imagens.acervo.aba.atuais')}
          <span
            className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
              !emLegado ? 'bg-white/20' : 'bg-schmersal-gray-light dark:bg-schmersal-gray-dark'
            }`}
          >
            {folders.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setAba('legado')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium ${
            emLegado
              ? 'bg-schmersal-blue text-white'
              : 'bg-white dark:bg-schmersal-gray-darker text-schmersal-gray-dark dark:text-schmersal-gray-light'
          }`}
          title={t('imagens.acervo.aba.legadoTitulo')}
        >
          <Archive className="h-4 w-4" />
          {t('imagens.acervo.aba.legado')}
          <span
            className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
              emLegado ? 'bg-white/20' : 'bg-schmersal-gray-light dark:bg-schmersal-gray-dark'
            }`}
          >
            {legadoFolders.length}
          </span>
        </button>

        {/* Importar acervo inteiro de uma vez, no acervo da aba aberta. */}
        <button
          type="button"
          onClick={() => zipInputRef.current?.click()}
          disabled={!!importandoZip}
          className="ml-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-schmersal-blue text-schmersal-blue dark:text-white text-sm font-medium disabled:opacity-50"
          title={t('imagens.upload.zip.botaoTitulo', {
            acervo: emLegado ? t('imagens.acervo.aba.legado') : t('imagens.acervo.aba.atuais'),
          })}
        >
          {importandoZip ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileArchive className="h-4 w-4" />}
          {importandoZip
            ? importandoZip.percentual < 100
              ? t('imagens.upload.zip.enviando', { percentual: importandoZip.percentual })
              : t('imagens.upload.zip.analisando')
            : t('imagens.upload.zip.botao')}
        </button>
      </div>

      {importandoZip && (
        <div className="rounded-lg border border-schmersal-blue/40 bg-schmersal-blue/5 p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-schmersal-blue dark:text-white">{importandoZip.nome}</span>
            <span className="text-schmersal-gray-medium">
              {importandoZip.percentual < 100
                ? t('imagens.upload.zip.percentualEnviado', { percentual: importandoZip.percentual })
                : t('imagens.upload.zip.lendoServidor')}
            </span>
          </div>
          <div className="h-2 rounded-full bg-schmersal-gray-light dark:bg-schmersal-gray-dark overflow-hidden">
            <div
              className={`h-full bg-schmersal-blue transition-all ${importandoZip.percentual >= 100 ? 'animate-pulse' : ''}`}
              style={{ width: `${Math.max(importandoZip.percentual, 3)}%` }}
            />
          </div>
        </div>
      )}

      {/* Conferência antes de gravar: um acervo leva quase uma hora de upload,
          então errar a organização e ter que reenviar sai caro. */}
      {analiseZip && (
        <div className="rounded-lg border border-schmersal-blue bg-white dark:bg-schmersal-gray-darker p-4 space-y-3">
          <div>
            <p className="font-semibold text-schmersal-blue dark:text-white">
              {t('imagens.upload.zip.confiraTitulo')}
            </p>
            <p className="text-sm text-schmersal-gray-medium">
              <TextoComDestaque
                texto={t('imagens.upload.zip.confiraResumo', {
                  fotos: analiseZip.totalFotos,
                  pastas: analiseZip.pastas.length,
                })}
                destaque={analiseZip.legado ? t('imagens.acervo.aba.legado') : t('imagens.acervo.aba.atuais')}
              />
              {analiseZip.ignorados > 0 &&
                t('imagens.upload.zip.confiraIgnorados', { quantidade: analiseZip.ignorados })}
            </p>
          </div>

          <div className="max-h-64 overflow-y-auto rounded border border-schmersal-gray-light dark:border-schmersal-gray-dark">
            <table className="w-full text-sm">
              <tbody>
                {analiseZip.pastas.map((p) => (
                  <tr key={p.pasta} className="border-b border-schmersal-gray-light/50 dark:border-schmersal-gray-dark/50">
                    <td className="px-3 py-1.5">{p.pasta}</td>
                    <td className="px-3 py-1.5 text-right text-schmersal-gray-medium whitespace-nowrap">
                      {t('imagens.upload.zip.pastaFotos', { quantidade: p.fotos })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {analiseZip.conflitos.length > 0 && (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              {t('imagens.upload.zip.conflitos', { pastas: analiseZip.conflitos.join(', ') })}
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleConfirmarZip}
              disabled={confirmandoZip}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-schmersal-blue text-white text-sm font-medium disabled:opacity-50"
            >
              {confirmandoZip ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleCheck className="h-4 w-4" />}
              {confirmandoZip ? t('imagens.upload.zip.importando') : t('imagens.upload.zip.importar')}
            </button>
            <button
              type="button"
              onClick={handleCancelarZip}
              disabled={confirmandoZip}
              className="px-4 py-2 rounded-lg border text-sm disabled:opacity-50"
            >
              {t('imagens.upload.zip.descartar')}
            </button>
            <span className="text-xs text-schmersal-gray-medium">
              {t('imagens.upload.zip.demora')}
            </span>
          </div>
        </div>
      )}

      {resultadoZip && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-4 text-sm text-emerald-800 dark:text-emerald-200 flex items-start gap-3">
          <CircleCheck className="h-5 w-5 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">
              {t('imagens.upload.zip.resultado', {
                fotos: resultadoZip.fotos,
                pastas: resultadoZip.pastas,
                novas: resultadoZip.pastasNovas,
              })}
            </p>
            {resultadoZip.ignorados > 0 && (
              <p>{t('imagens.upload.zip.resultadoIgnorados', { quantidade: resultadoZip.ignorados })}</p>
            )}
            {resultadoZip.conflitos.length > 0 && (
              <p>{t('imagens.upload.zip.resultadoConflitos', { pastas: resultadoZip.conflitos.join(', ') })}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setResultadoZip(null)}
            className="ml-auto"
            title={t('comum.acao.fechar')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {emLegado && legadoFolders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-schmersal-gray-light/60 dark:bg-schmersal-gray-dark text-schmersal-gray-medium">
                <Archive className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-semibold leading-tight text-schmersal-gray-dark dark:text-white">
                  {legadoStats.total}
                  <span className="ml-1.5 text-xs font-medium text-schmersal-gray-medium">
                    {t('imagens.revisaoLegado.metricas.revisado', { percentual: legadoStats.percentual })}
                  </span>
                </p>
                <p className="text-xs text-schmersal-gray-medium">
                  {t('imagens.revisaoLegado.metricas.maquinas')}
                </p>
              </div>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-schmersal-blue/15 overflow-hidden">
              <div
                className="h-full rounded-full bg-schmersal-blue transition-all"
                style={{ width: `${legadoStats.percentual}%` }}
              />
            </div>
          </div>

          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <CircleCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-semibold leading-tight text-schmersal-gray-dark dark:text-white">
                {legadoStats.concluidas}
              </p>
              <p className="text-xs text-schmersal-gray-medium">
                {t('imagens.revisaoLegado.metricas.aprovadas', { fotos: legadoStats.aprovadas })}
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400">
              <TriangleAlert className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-semibold leading-tight text-schmersal-gray-dark dark:text-white">
                {legadoStats.precisaMaisImagens}
              </p>
              <p className="text-xs text-schmersal-gray-medium">
                {t('imagens.revisaoLegado.metricas.precisamImagens', { fotos: legadoStats.reprovadas })}
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <Clock className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-semibold leading-tight text-schmersal-gray-dark dark:text-white">
                {legadoStats.pendentes}
              </p>
              <p className="text-xs text-schmersal-gray-medium">
                {t('imagens.revisaoLegado.metricas.aRevisar', { maquinas: naoRevisadasCount })}
              </p>
            </div>
          </div>
        </div>
      )}

      {!emLegado && folders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-schmersal-gray-light/60 dark:bg-schmersal-gray-dark text-schmersal-gray-medium">
              <Folders className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-semibold leading-tight text-schmersal-gray-dark dark:text-white">
                {apreciacaoStats.total}
              </p>
              <p className="text-xs text-schmersal-gray-medium">
                {t('imagens.acervo.metricas.totalMaquinas')}
                {apreciacaoStats.canceladas > 0 &&
                  t('imagens.acervo.metricas.canceladasSufixo', { quantidade: apreciacaoStats.canceladas })}
                {apreciacaoStats.naoNr12 > 0 &&
                  t('imagens.acervo.metricas.naoNr12Sufixo', { quantidade: apreciacaoStats.naoNr12 })}
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-schmersal-blue/10 text-schmersal-blue">
                <ClipboardCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-semibold leading-tight text-schmersal-gray-dark dark:text-white">
                  {apreciacaoStats.comApreciacao}
                  <span className="ml-1.5 text-xs font-medium text-schmersal-gray-medium">
                    ({apreciacaoStats.percentual}%)
                  </span>
                </p>
                <p className="text-xs text-schmersal-gray-medium">{t('imagens.acervo.metricas.comApreciacao')}</p>
              </div>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-schmersal-blue/15 overflow-hidden">
              <div
                className="h-full rounded-full bg-schmersal-blue transition-all"
                style={{ width: `${apreciacaoStats.percentual}%` }}
              />
            </div>
          </div>

          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-semibold leading-tight text-schmersal-gray-dark dark:text-white">
                {apreciacaoStats.semApreciacao}
              </p>
              <p className="text-xs text-schmersal-gray-medium">{t('imagens.acervo.metricas.semApreciacao')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-schmersal-gray-darker p-4 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2 flex gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('imagens.acervo.busca.placeholder')}
                className="w-full pl-9 pr-3 py-2.5 border rounded-lg bg-white dark:bg-schmersal-gray-darkest"
              />
            </div>
            <button
              type="button"
              onClick={() => (emLegado ? setShowNaoRevisadas((v) => !v) : setShowPendentes((v) => !v))}
              className={`inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium whitespace-nowrap shrink-0 ${
                (emLegado ? showNaoRevisadas : showPendentes)
                  ? 'bg-schmersal-blue text-white border-schmersal-blue'
                  : 'bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-schmersal-gray-light border-schmersal-gray-light dark:border-schmersal-gray-dark hover:border-schmersal-blue'
              }`}
              title={
                emLegado ? t('imagens.acervo.filtro.aRevisarTitulo') : t('imagens.acervo.filtro.pendentesTitulo')
              }
            >
              <Filter className="h-4 w-4" />
              {emLegado ? t('imagens.acervo.filtro.aRevisar') : t('imagens.acervo.filtro.pendentes')}
              <span
                className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                  (emLegado ? showNaoRevisadas : showPendentes)
                    ? 'bg-white/20'
                    : 'bg-schmersal-gray-light dark:bg-schmersal-gray-dark'
                }`}
              >
                {emLegado ? naoRevisadasCount : pendentesCount}
              </span>
            </button>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') createFolder();
              }}
              placeholder={
                emLegado ? t('imagens.acervo.busca.novaMaquinaLegado') : t('imagens.acervo.busca.novaPasta')
              }
              className="w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-schmersal-gray-darkest"
            />
            <button
              type="button"
              onClick={createFolder}
              disabled={savingFolder || !newFolderName.trim()}
              className="inline-flex items-center px-3 py-2.5 rounded-lg bg-schmersal-blue text-white disabled:opacity-50"
            >
              {savingFolder ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderPlus className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="h-44 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" />
        </div>
      ) : emLegado ? (
        filteredLegado.length === 0 ? (
          <div className="p-8 text-center rounded-lg border border-dashed border-schmersal-gray-light text-schmersal-gray-medium">
            {showNaoRevisadas
              ? t('imagens.revisaoLegado.vazio.semPendentes')
              : legadoFolders.length === 0
              ? t('imagens.revisaoLegado.vazio.semMaquinas')
              : t('imagens.revisaoLegado.vazio.semResultados')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredLegado.map((folder) => renderLegadoCard(folder))}
          </div>
        )
      ) : filteredFolders.length === 0 ? (
        <div className="p-8 text-center rounded-lg border border-dashed border-schmersal-gray-light text-schmersal-gray-medium">
          {showPendentes ? t('imagens.acervo.vazio.semPendentes') : t('imagens.acervo.vazio.semPastas')}
        </div>
      ) : viewMode === 'list' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredFolders.map((folder) => renderFolderCard(folder))}
        </div>
      ) : (
        <div className="space-y-4">
          {groupedByArea.map(([area, areaFolders]) => {
            const collapsed = collapsedAreas.has(area);
            return (
              <div
                key={area}
                className="bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleArea(area)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-schmersal-gray-light/50 dark:hover:bg-schmersal-gray-dark/40"
                >
                  <span className="flex items-center gap-2">
                    {collapsed ? (
                      <ChevronRight className="h-4 w-4 text-schmersal-gray-medium" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-schmersal-gray-medium" />
                    )}
                    <span className="font-semibold text-schmersal-gray-dark dark:text-white">
                      {area === SEM_AREA ? t('imagens.acervo.semArea') : area}
                    </span>
                  </span>
                  <span className="text-xs text-schmersal-gray-medium">
                    {t('imagens.acervo.grupo.maquinas', { quantidade: areaFolders.length })}
                  </span>
                </button>
                {!collapsed && (
                  <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {areaFolders.map((folder) => renderFolderCard(folder))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!emLegado && semProjetoFolders.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          {/* Não confundir com a aba Legado: aqui são pastas soltas no disco,
              gravadas antes da separação por projeto. */}
          <h2 className="font-semibold text-amber-900">{t('imagens.acervo.semProjetoLista.titulo')}</h2>
          <p className="text-xs text-amber-800 mt-1">{t('imagens.acervo.semProjetoLista.descricao')}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-amber-900">
            {semProjetoFolders.map((folder) => (
              <span key={folder.machineFolder} className="px-2 py-1 rounded bg-amber-100 border border-amber-300">
                {folder.machineName} ({folder.totalFotos})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Modal: escolher template ou criar do zero */}
      {apreciacaoModalFolder && (
        <div className="fixed inset-0 z-[55] bg-black/60 p-4 flex items-center justify-center">
          <div className="bg-white dark:bg-schmersal-gray-darker w-full max-w-lg rounded-lg overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg text-schmersal-gray-dark dark:text-white">
                  {folderApreciacaoIds(apreciacaoModalFolder).length > 0
                    ? t('imagens.acervo.apreciacaoModal.tituloExistentes')
                    : t('imagens.acervo.apreciacaoModal.tituloNova')}
                </h3>
                <p className="text-xs text-schmersal-gray-medium">
                  {t('imagens.acervo.apreciacaoModal.maquina', { nome: apreciacaoModalFolder.machineName })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !creatingApreciacao && setApreciacaoModalFolder(null)}
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-schmersal-gray-dark"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 overflow-auto space-y-4">
              {folderApreciacaoIds(apreciacaoModalFolder).length > 0 && (
                <div className="rounded-lg border border-schmersal-blue/40 bg-schmersal-blue/5 p-3">
                  <p className="text-sm font-medium text-schmersal-gray-dark dark:text-white flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-schmersal-blue" />
                    {folderApreciacaoIds(apreciacaoModalFolder).length === 1
                      ? t('imagens.acervo.apreciacaoModal.vinculadaUma')
                      : t('imagens.acervo.apreciacaoModal.vinculadaVarias', {
                          quantidade: folderApreciacaoIds(apreciacaoModalFolder).length,
                        })}
                  </p>
                  <div className="mt-2 space-y-2">
                    {folderApreciacaoIds(apreciacaoModalFolder).map((id) => {
                      const ap = apreciacoesExistentes.find((a) => a.id === id);
                      const semNome = t('imagens.acervo.apreciacaoModal.semNome');
                      const nome = ap
                        ? `${ap.numero ? `${ap.numero} — ` : ''}${ap.nomeMaquina || ap.maquina?.nome || ap.titulo || semNome}`
                        : semNome;
                      return (
                        <div
                          key={id}
                          className="flex items-center gap-2 rounded border border-schmersal-blue/30 bg-white dark:bg-schmersal-gray-darkest px-2 py-1.5"
                        >
                          <ClipboardCheck className="h-4 w-4 shrink-0 text-schmersal-blue" />
                          <span className="flex-1 min-w-0 truncate text-sm text-schmersal-gray-dark dark:text-white">
                            {nome}
                          </span>
                          <button
                            type="button"
                            onClick={() => navigate(`/apreciacoes/${id}/editar`)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-schmersal-blue text-white text-xs"
                            title={t('imagens.acervo.apreciacaoModal.editarTitulo')}
                          >
                            <Pencil className="h-3.5 w-3.5" /> {t('comum.acao.editar')}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleVinculoApreciacao(apreciacaoModalFolder, id, true)}
                            disabled={creatingApreciacao}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 text-xs disabled:opacity-50"
                            title={t('imagens.acervo.apreciacaoModal.removerVinculo')}
                          >
                            <X className="h-3.5 w-3.5" /> {t('comum.acao.remover')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-schmersal-gray-medium">
                    {t('imagens.acervo.apreciacaoModal.vincularMais')}
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  const folder = apreciacaoModalFolder;
                  setApreciacaoModalFolder(null);
                  abrirAssistenteIA(folder);
                }}
                disabled={creatingApreciacao || !podeGerarComIA(apreciacaoModalFolder).ok}
                className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-purple-500 bg-purple-50 text-purple-800 hover:bg-purple-100 disabled:opacity-50"
                title={podeGerarComIA(apreciacaoModalFolder).motivo}
              >
                <Sparkles className="h-5 w-5 shrink-0 text-purple-600" />
                <span className="text-left">
                  <span className="block font-semibold">{t('imagens.encaixe.cartaoTitulo')}</span>
                  <span className="block text-xs text-purple-700">
                    {t('imagens.encaixe.cartaoDescricao', { fotos: apreciacaoModalFolder.totalFotos })}
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => criarApreciacaoDaPasta(apreciacaoModalFolder)}
                disabled={creatingApreciacao}
                className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-schmersal-blue/50 text-schmersal-blue hover:bg-schmersal-blue/5 disabled:opacity-50"
              >
                <FilePlus2 className="h-5 w-5 shrink-0" />
                <span className="text-left">
                  <span className="block font-semibold">{t('imagens.acervo.apreciacaoModal.criarDoZero')}</span>
                  <span className="block text-xs text-schmersal-gray-medium">
                    {t('imagens.acervo.apreciacaoModal.criarDoZeroDescricao')}
                  </span>
                </span>
              </button>

              {templatesMaquina.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-schmersal-gray-medium uppercase tracking-wide mb-2">
                    {t('imagens.acervo.apreciacaoModal.modelosMaquina')}
                  </p>
                  <div className="space-y-2 max-h-72 overflow-auto pr-1">
                    {templatesMaquina.map((template) => {
                      const tipo = template.tipoMaquina && template.tipoMaquina.trim();
                      const detalhe = [
                        template.grupo?.nome,
                        tipo && tipo.toLowerCase() !== 'não especificado' ? tipo : null,
                      ]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => criarApreciacaoComModeloMaquina(apreciacaoModalFolder, template.id)}
                          disabled={creatingApreciacao}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark hover:border-schmersal-blue hover:bg-schmersal-blue/5 disabled:opacity-50"
                        >
                          <LayoutGrid className="h-5 w-5 shrink-0 text-schmersal-blue" />
                          <span className="text-left min-w-0">
                            <span className="block font-medium text-schmersal-gray-dark dark:text-white truncate">
                              {template.nome}
                            </span>
                            <span className="block text-xs text-schmersal-gray-medium">
                              {t('imagens.acervo.apreciacaoModal.modeloDetalhe', {
                                partes: template.totalPartes ?? 0,
                                riscos: template.totalRiscos ?? 0,
                              })}
                              {detalhe ? t('imagens.acervo.apreciacaoModal.modeloDetalheSufixo', { detalhe }) : ''}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-schmersal-gray-medium uppercase tracking-wide mb-2">
                  {t('imagens.acervo.apreciacaoModal.templatesSimples')}
                </p>
                {templates.length === 0 ? (
                  <p className="text-sm text-schmersal-gray-medium">
                    {t('imagens.acervo.apreciacaoModal.semTemplates')}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-auto pr-1">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => criarApreciacaoDaPasta(apreciacaoModalFolder, template.id)}
                        disabled={creatingApreciacao}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark hover:border-schmersal-blue hover:bg-schmersal-blue/5 disabled:opacity-50"
                      >
                        <FileText className="h-5 w-5 shrink-0 text-schmersal-blue" />
                        <span className="text-left">
                          <span className="block font-medium text-schmersal-gray-dark dark:text-white">
                            {template.nome}
                          </span>
                          {template.tipo && (
                            <span className="block text-xs text-schmersal-gray-medium">{template.tipo}</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Vincular a uma ou mais apreciações já existentes (toggle, multi-seleção) */}
              <div>
                <p className="text-xs font-semibold text-schmersal-gray-medium uppercase tracking-wide mb-2">
                  {t('imagens.acervo.apreciacaoModal.vincularExistentes')}
                </p>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium" />
                  <input
                    type="text"
                    value={apreciacaoSearch}
                    onChange={(e) => setApreciacaoSearch(e.target.value)}
                    placeholder={t('imagens.acervo.apreciacaoModal.buscarPlaceholder')}
                    className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white dark:bg-schmersal-gray-darkest"
                  />
                </div>
                {loadingApreciacoes ? (
                  <div className="flex items-center gap-2 text-sm text-schmersal-gray-medium py-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> {t('imagens.acervo.apreciacaoModal.carregando')}
                  </div>
                ) : (
                  (() => {
                    const q = apreciacaoSearch.trim().toLowerCase();
                    const lista = apreciacoesExistentes
                      .filter((a) => {
                        if (!q) return true;
                        return [a.numero, a.titulo, a.nomeMaquina, a.maquina?.nome, a.maquina?.area]
                          .filter(Boolean)
                          .some((v) => (v as string).toLowerCase().includes(q));
                      })
                      .slice(0, 30);
                    if (apreciacoesExistentes.length === 0) {
                      return (
                        <p className="text-sm text-schmersal-gray-medium">
                          {t('imagens.acervo.apreciacaoModal.semApreciacoes')}
                        </p>
                      );
                    }
                    if (lista.length === 0) {
                      return (
                        <p className="text-sm text-schmersal-gray-medium">
                          {t('imagens.acervo.apreciacaoModal.semResultados')}
                        </p>
                      );
                    }
                    const vinculadas = new Set(folderApreciacaoIds(apreciacaoModalFolder));
                    return (
                      <div className="space-y-2 max-h-60 overflow-auto">
                        {lista.map((a) => {
                          const jaVinculada = vinculadas.has(a.id);
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => toggleVinculoApreciacao(apreciacaoModalFolder, a.id, jaVinculada)}
                              disabled={creatingApreciacao}
                              className={`w-full flex items-center gap-3 p-3 rounded-lg border disabled:opacity-50 ${
                                jaVinculada
                                  ? 'border-schmersal-blue bg-schmersal-blue/10'
                                  : 'border-schmersal-gray-light dark:border-schmersal-gray-dark hover:border-schmersal-blue hover:bg-schmersal-blue/5'
                              }`}
                            >
                              {jaVinculada ? (
                                <Check className="h-5 w-5 shrink-0 text-schmersal-blue" />
                              ) : (
                                <Link2 className="h-5 w-5 shrink-0 text-schmersal-blue" />
                              )}
                              <span className="text-left min-w-0 flex-1">
                                <span className="block font-medium text-schmersal-gray-dark dark:text-white truncate">
                                  {a.numero ? `${a.numero} — ` : ''}
                                  {a.nomeMaquina ||
                                    a.maquina?.nome ||
                                    a.titulo ||
                                    t('imagens.acervo.apreciacaoModal.semNome')}
                                </span>
                                <span className="block text-xs text-schmersal-gray-medium truncate">
                                  {a.maquina?.area || a.titulo || ''}
                                </span>
                              </span>
                              <span className={`text-xs font-medium shrink-0 ${jaVinculada ? 'text-red-600' : 'text-schmersal-blue'}`}>
                                {jaVinculada ? t('comum.acao.remover') : t('imagens.acervo.apreciacaoModal.vincular')}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>

            {creatingApreciacao && (
              <div className="p-4 border-t flex items-center justify-center gap-2 text-sm text-schmersal-gray-medium">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('imagens.acervo.apreciacaoModal.processando')}
              </div>
            )}
          </div>
        </div>
      )}

      {openedFolder && (
        <div className="fixed inset-0 z-50 bg-black/60 p-4 md:p-8 flex items-center justify-center">
          <div className="bg-white dark:bg-schmersal-gray-darker w-full max-w-5xl max-h-[90vh] rounded-lg overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">{openedFolder.machineName}</h3>
                <p className="text-xs text-schmersal-gray-medium">
                  {t('imagens.acervo.card.fotos', {
                    quantidade: openedFolder.totalFotos - (openedFolder.totalVideos ?? 0),
                  })}
                  {openedFolder.totalVideos
                    ? t('imagens.acervo.card.videosSufixo', { quantidade: openedFolder.totalVideos })
                    : ''}
                </p>
                <DataLevantamento folder={openedFolder} />
              </div>
              <button type="button" onClick={() => setOpenedFolder(null)} className="p-2 rounded hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            {openedFolder.fotos.length === 0 ? (
              <div className="p-10 flex flex-col items-center justify-center gap-2 text-schmersal-gray-medium">
                <ImageOff className="h-10 w-10 opacity-50" />
                <p className="text-sm">{t('imagens.acervo.galeria.vazioTitulo')}</p>
                <p className="text-xs">{t('imagens.acervo.galeria.vazioDica')}</p>
              </div>
            ) : (
            <div className="p-4 overflow-auto grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {openedFolder.fotos.map((foto) => (
                <div key={foto.nomeArquivo} className="relative border rounded overflow-hidden hover:shadow">
                  <button
                    type="button"
                    onClick={() => (foto.tipo === 'video' ? setOpenedVideo(foto) : setOpenedPhoto(foto))}
                    className="w-full text-left"
                    title={
                      foto.tipo === 'video'
                        ? t('imagens.acervo.galeria.abrirVideo')
                        : t('imagens.acervo.galeria.abrirFoto')
                    }
                  >
                    <div className="relative">
                      <img
                        src={withPreviewParams(toAbsoluteUrl(foto.previewUrl), 500, 60)}
                        alt={foto.nomeArquivo}
                        loading="lazy"
                        className="w-full h-36 object-cover"
                      />
                      {foto.tipo === 'video' && (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                          <span className="rounded-full bg-black/60 p-2.5">
                            <Play className="h-6 w-6 text-white" fill="currentColor" />
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium truncate">{foto.nomeArquivo}</p>
                      <p className="text-[11px] text-schmersal-gray-medium">
                        {formatBytes(foto.tamanho, formatarNumero)}
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeletePhoto(openedFolder, foto)}
                    disabled={deletingPhoto === foto.nomeArquivo}
                    className="absolute top-1.5 right-1.5 p-1.5 rounded bg-white/90 border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    title={t('imagens.acervo.galeria.apagarFoto')}
                  >
                    {deletingPhoto === foto.nomeArquivo ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>
      )}

      {openedPhoto && (
        <div className="fixed inset-0 z-[60] bg-black/85 p-4 flex items-center justify-center">
          <button
            type="button"
            className="absolute top-4 right-4 p-2 rounded bg-white/20 text-white"
            onClick={() => setOpenedPhoto(null)}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={toAbsoluteUrl(openedPhoto.url)}
            alt={openedPhoto.nomeArquivo}
            className="max-w-[95vw] max-h-[92vh] object-contain"
          />
        </div>
      )}

      {openedVideo && openedFolder && projeto?.id && (
        <VisualizadorVideoModal
          projetoId={projeto.id}
          machineFolder={openedFolder.machineFolder}
          foto={openedVideo}
          videoUrl={toAbsoluteUrl(openedVideo.url)}
          onFechar={() => setOpenedVideo(null)}
          // O quadro vira foto da pasta: recarrega para ele aparecer na grade.
          onQuadroExtraido={() => { void loadFolders(); }}
        />
      )}

      {/* Confirmação de pré-voo: última chance de corrigir a máquina antes de
          gerar, já que a apreciação é gravada direto como rascunho. */}
      {pastaParaIA && projeto?.id && (
        <ConfirmarGeracaoIAModal
          aberto
          projetoId={projeto.id}
          machineFolder={pastaParaIA.machineFolder}
          machineName={pastaParaIA.machineName}
          jaTemApreciacao={folderApreciacaoIds(pastaParaIA).length > 0}
          onFechar={() => setPastaParaIA(null)}
          onIniciado={(jobId) => {
            setJobIAAtivo({ jobId, machineName: pastaParaIA.machineName });
            setPastaParaIA(null);
            void loadFolders();
          }}
        />
      )}

      {/* Revisão do legado: entra na máquina e dá o veredito foto a foto. */}
      {(() => {
        const folder = revisandoFolder
          ? legadoFolders.find((item) => item.machineFolder === revisandoFolder)
          : null;
        if (!folder) return null;
        return (
          <RevisaoLegadoModal
            folder={folder}
            projetoId={projeto?.id}
            apagandoFoto={deletingPhoto}
            onRevisar={(fileName, status, motivo) => revisarFoto(folder, fileName, status, motivo)}
            onFechar={() => setRevisandoFolder(null)}
            onAdicionarFotos={() => handleSelectFiles(folder, false)}
            onApagarFoto={(foto) => handleDeletePhoto(folder, foto)}
          />
        );
      })()}

      {jobIAAtivo && (
        <JobProgressModal
          aberto
          jobId={jobIAAtivo.jobId}
          machineName={jobIAAtivo.machineName}
          onFechar={() => {
            setJobIAAtivo(null);
            void loadFolders();
          }}
          onConcluido={() => void loadFolders()}
        />
      )}
    </div>
  );
}
